import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import ApprovedContainer from './../ApprovedContainer/ApprovedContainer.jsx';
import RewardPointsAndBonusContainer from 'src/allRewardsAdmin/container/rewardPointsAndBonusContainer'
import { message, Modal, Button } from 'antd';
import { withApollo } from 'react-apollo';
import { motion } from 'motion/react';
import { gql, useQuery } from '@apollo/client';
import { addCommas } from 'src/rewardsAndRecognitionHelper/utils.js';
import { fetchModernFormQuestions } from 'src/_shared/services/utils.js';
import { useSelector } from 'react-redux';
import { useApprovalRewardsService } from '../../service/ApprovalRewards.service.jsx';

// import './style.scss'
import { ReactComponent as PointsToSendIcon } from 'src/assets/svg/pointstosend.svg';
import { ReactComponent as RecipientIcon } from 'src/assets/svg/recipientOne.svg';
import { ReactComponent as RewardIcon } from 'src/assets/svg/rewardOne.svg';
import { ReactComponent as CalendarIcon } from 'src/assets/svg/calendar.svg';
import { ReactComponent as CalendarSilhouetteIcon } from 'src/assets/svg/calendar-silhouette.svg';


const QUERY_BADGE_ASSIGNMENTS = gql`
  query QueryBadgeAssignments($companyId: ID!, $nextToken: String) {
    queryBadgeAssignments(
      companyId: $companyId
      nextToken: $nextToken
    ) {
      items {
        approvalStatus
        awardedBy
        awardedByUser {
          id
          firstName
          lastName
          emailAddress
        }
        id
        badgeId
        badge {
            id
            name
			description
			points
			bonusPoints
			label
			badgeTypeId
			iconId
			color
			iconColor
			gradientColor
            snappyGrantId
            snappyGrant {
    			id
                name
                costInCurrency
				externalSnappyId
				snappyCampaignTierId
            }
        }
		content {
			message
			gifURL
		}
        rewardBonus
        companyId
        userGroupId
        justificationFormAnswers
        userId
        createdAt
        updatedAt
        completedAt
        user {
          id
          firstName
          lastName
          emailAddress
          recognitionEvents {
            currentBudget
          }
        }
		declineReason
      }
      nextToken
    }
  }
`;

const UPDATE_BADGE_ASSIGNMENT = gql`
	mutation UpdateBadgeAssignment($input: UpdateBadgeAssignmentInput!) {
		updateBadgeAssignment(input: $input) {
			id
			approvalStatus
			sentWithoutSnappyGrant
			completedAt
		}
	}
`;

const UPDATE_USER = gql`
	mutation UpdateUser($input: UpdateUserInput!) {
		updateUser(input: $input) {
			id
			recognitionEvents {
				currentBudget
				amountPending
				additionalBudgetTotal
				lastUpdated
				eventList {
					adminOverride
					approvalResult
					budgetAfterTotal
					budgetBeforeTotal
					completedById
					completedDate
					createdDate
					pendingApproval
					recognitionAmount
					recognitionId
					recognitionName
					sentById
					sentByManager
					sentReason
					snappyCampaignTierId
				}
			}
		}
	}
`;

const CREATE_SNAPPY_GIFT = gql`
	mutation CreateSnappyGift($input: CreateSnappyGiftInput!) {
		createSnappyGift(input: $input) {
			id
			giftId
			campaignTierId
			snappyGrantId
			status
			giftDetails {
				link
				status
			}
			createdAt
		}
	}
`;

const ApprovalRewardsContainer = (props) => {
	const currentUser = useSelector((state) => state.user.currentUser);
	const companyId = currentUser?.company?.id;
	const approvalRewardsService = useApprovalRewardsService();
	const [formData, setFormData] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [pendingRecordId, setPendingRecordId] = useState(null);
	const [pendingRecord, setPendingRecord] = useState(null);
	const [isApprove, setIsApprove] = useState(false);
	const [isDecline, setIsDecline] = useState(false);
	const [isBudgetModal, setIsBudgetModal] = useState(false);
	const [allRewardsInfo, setAllRewardsInfo] = useState({});

	useEffect(() => {
		const getRewardsInfo = async () => {
			try {
				const response = await approvalRewardsService.getAllRewards();
		
				if (response?.code === 200 && response?.data) {
					setAllRewardsInfo(response.data);
				}
			} catch (e) {
				console.log(e);
			}
		};
		getRewardsInfo();
	}, []);
	
	const badgeSummary = [
		{
			cardColor: 'oklch(0.6757 0.187 349.98)',
			pointAmount: allRewardsInfo?.totalPoints,
			cardTitle: 'Total Points',
			Icon: PointsToSendIcon,
		},
		{
			cardColor: 'oklch(0.7903 0.136 192.97)',
			pointAmount: allRewardsInfo?.peerToPeerBadges,
			cardTitle: 'On-The-Spot Recognitions',
			Icon: RewardIcon,
		},
		{
			cardColor: 'oklch(0.58 0.26 295)',
			pointAmount: allRewardsInfo?.anniversaryBadges,
			cardTitle: 'Milestones',
			Icon: CalendarSilhouetteIcon,
		},
	];

	const engagementSummary = [
		{
			cardColor: 'oklch(0.2998 0.034 260.85)',
			pointAmount: allRewardsInfo?.Employees,
			cardTitle: 'Employees',
			Icon: PointsToSendIcon,
		},
		{
			cardColor: 'oklch(0.39 0.159 24.91)',
			pointAmount: allRewardsInfo?.allTimeShoutouts,
			cardTitle: <>Shoutout <br />(All Time)</>,
			Icon: RecipientIcon,
		},
		{
			cardColor: 'oklch(0.6144 0.1809 25.93)',
			pointAmount: allRewardsInfo?.lastThirtyDaysShoutouts,
			cardTitle: <>Shoutouts <br /> (Last 30 Days)</>,
			Icon: CalendarIcon,
		},
	];
	const { loading, error, data, fetchMore } = useQuery(
		QUERY_BADGE_ASSIGNMENTS,
		{
			variables: {
				companyId: companyId,
				nextToken: null
			},
			skip: !companyId
		}
	);
	
	useEffect(() => {
		if (data?.queryBadgeAssignments?.nextToken && !error) {
			fetchMore({
				variables: {companyId: companyId, nextToken: data.queryBadgeAssignments.nextToken},
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult) return prev;
					return {
						queryBadgeAssignments: {
							...fetchMoreResult.queryBadgeAssignments,
							items: [
								...prev.queryBadgeAssignments.items,
								...fetchMoreResult.queryBadgeAssignments.items
							]
						}
					};
				}
			});
		}
	}, [data?.queryBadgeAssignments?.nextToken, fetchMore, companyId, error]);

	useEffect(() => {
		if (!isLoading && !formData) {
			const awaitFormData = async () => {
				try {
					setIsLoading(true);
					const awaitedFormData = await fetchModernFormQuestions(				
						currentUser,
						'custom',
						'manager_justification_form'
					) || {};
					setFormData(awaitedFormData);
				} catch (e) {
					throw e;
				} finally {
					setIsLoading(false);
				}	
			};					
			awaitFormData();
		}
	}, [companyId]);

	// All computation and hooks must be before any early returns
	const getJustificationContent = useCallback((justificationData = null) => {
		if (justificationData === null) return [];
		const parsedJustificationData = JSON.parse(justificationData);
		const content = [];
		const validElements = ['input','textarea','select'];
		formData?.elements?.forEach(ele => {
			if (validElements.includes(ele?.type)) {
				const answer = parsedJustificationData?.[ele.id] || "";
				content.push({question: ele?.label, answer});
			}
		});
		return content;
	}, [formData]);

	const items = data?.queryBadgeAssignments?.items || [];
	const pendingAssignments = useMemo(() => items.filter(item => item.approvalStatus === 'Pending').sort((a, b) => a.createdAt - b.createdAt), [items]);
	const approvedAssignments = useMemo(() => items.filter(item => item.approvalStatus === 'Approved'), [items]);
	const declinedAssignments = useMemo(() => items.filter(item => item.approvalStatus === 'Declined'), [items]);
	const approvedDeclinedAssignments = useMemo(() => [...approvedAssignments, ...declinedAssignments].sort((a, b) => a.createdAt - b.createdAt), [approvedAssignments, declinedAssignments]);
	const totalBonus = useMemo(() => items.reduce((sum, item) => sum + (item.badge?.snappyGrant?.costInCurrency || 0), 0), [items]);
	const approvedBonus = useMemo(() => items
		.filter(item => item.approvalStatus === 'Approved')
		.reduce((sum, item) => sum + (item.badge?.snappyGrant?.costInCurrency || 0), 0), [items]);
	const pendingBonus = useMemo(() => pendingAssignments
		.reduce((sum, item) => sum + (item.badge?.snappyGrant?.costInCurrency || 0), 0), [pendingAssignments]);
	
	const countData = useMemo(() => ({
		approvalStatusCounts: [
			{ name: 'Approved', totals: approvedAssignments.length, color: 'oklch(0.7903 0.136 192.97)' }, // P3 colorspace of #14D6D3
			{ name: 'Pending', totals: pendingAssignments.length, color: 'oklch(0.6757 0.187 349.98)' }, // P3 colorspace of #E75DA7
			{ name: 'Declined', totals: declinedAssignments.length, color: 'oklch(0.58 0.26 295)' } // P3 colorspace of #7942E7
		],
		rewardPointsValues: [
			{ name: 'Total Rewards', totals: totalBonus, color: 'oklch(0.5307 0.1791 144.1)' }, // P3 colorspace of #1D8627
			{ name: 'Approved', totals: approvedBonus, color: 'oklch(0.7099 0.1414 141.23)' },
			{ name: 'Pending', totals: pendingBonus, color: 'oklch(0.8462 0.1426 141.19)' }
		]
	}), [approvedAssignments, pendingAssignments, declinedAssignments, totalBonus, approvedBonus, pendingBonus]);

	const SummaryStatBox = useCallback(({ color = '', statName, label, total, title, index }) => {
		const motionProps = {
			initial: { opacity: 0, scale: 0.9 },
			animate: { opacity: 1, scale: 1 },
			transition: { duration: 0.5, delay: (index + 1) / 10, ease: "easeOut" },
			whileHover: { scale: 1.02, transition: { duration: 0.2 } }
		};

		const cardStyle = {
			flex: 1,
			boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
			width: '33.33333%',
			background: color,
		};

		return (
			<motion.div
				className="color-card"
				style={cardStyle}
				{...motionProps}
			>
				<h4>
					{' '}
					{title === ' Bonuses' && `$`} {addCommas(total)}
				</h4>
				<p>
					{label
						? label
						: statName === 'Total Rewards'
							? 'Total'
							: statName}
				</p>
			</motion.div>
		);
	}, []);

	const motionContainerProps = useMemo(() => ({
		initial: { opacity: 0, y: 20 },
		animate: { opacity: 1, y: 0 },
		transition: { duration: 0.6, ease: "easeOut" }
	}), []);

	const approvalStatusBoxes = useMemo(() => {
		if (!countData?.approvalStatusCounts?.length && !loading) return null;
		if (!countData?.approvalStatusCounts) return null;
		return countData?.approvalStatusCounts.map((reward, index) => (
			<SummaryStatBox
				key={`approval-${reward.id || index}`}
				color={reward.color}
				statName={reward.name}
				total={reward.totals}
				index={index}
			/>
		));
	}, [countData?.approvalStatusCounts, loading, SummaryStatBox]);

	const rewardPointsBoxes = useMemo(() => {
		if (!countData?.rewardPointsValues?.length && !loading) return null;
		if (!countData?.rewardPointsValues) return null;
		return countData.rewardPointsValues.map((reward, index) => (
			<SummaryStatBox
				key={`reward-${reward.id || index}`}
				color={reward.color}
				statName={reward.name}
				total={`$${reward.totals}`}
				index={index}
			/>
		));
	}, [countData?.rewardPointsValues, loading, SummaryStatBox]);

	const approvalStatusSection = useMemo(() => (
		<motion.div
			className="contact-color-card"
			style={{}}
			{...motionContainerProps}
		>
			{approvalStatusBoxes}
		</motion.div>
	), [approvalStatusBoxes, motionContainerProps]);

	const rewardPointsSection = useMemo(() => (
		<motion.div
			className="contact-color-card"
			style={{}}
			{...motionContainerProps}
		>
			{rewardPointsBoxes}
		</motion.div>
	), [rewardPointsBoxes, motionContainerProps]);

	if (error) return <div>Error loading data: {error.message}</div>;
	if (loading) return <div></div>;
	if (isLoading) return <div></div>;

	const handleApproveAndDeclined = async (recordId, status, record = null, declineReason = null) => {
		if (!recordId) {
			message.error('Record ID not found');
			return;
		}

		if (status === 'Declined') {
			try {
				const { data } = await props.client.mutate({
					mutation: UPDATE_BADGE_ASSIGNMENT,
					variables: {
						input: {
							id: recordId,
							companyId: companyId,
							approvalStatus: 'Declined',
							completedAt: new Date().toISOString(),
							declineReason: declineReason
						}
					}
				});

				const requestBody = {
					approvalStatus: 'Declined'
				};
				
				try {
					await approvalRewardsService.updateApprovalStatus(recordId, requestBody);
				} catch (apiError) {
					console.error('Error updating approval status via API:', apiError);
				}

				if (data?.updateBadgeAssignment) {
					setIsDecline(false);
					setIsApprove(false);
					message.success('Recognition declined successfully');
				} else {
					message.error('Failed to decline recognition');
				}
			} catch (error) {
				console.error('Error declining recognition:', error);
				message.error('Failed to decline recognition');
			}
			return;
		}

		if (status === 'Approved') {
			const currentRecord = record || pendingRecord;
			if (!currentRecord) {
				message.error('Record data not found');
				return;
			}

			const remainingBudget = currentRecord.remainingBudget || 0;
			const totalCost = currentRecord.costInCurrency || 0;

			if (remainingBudget < totalCost && totalCost > 0) {
				setIsBudgetModal(true);
				return;
			}

			await approveWithGift(recordId, currentRecord);
		}
	};

	const approveWithGift = async (recordId, record, sentWithoutSnappyGrant = false, overrideBudget = false) => {
		try {
			const updateInput = {
				id: recordId,
				companyId: companyId,
				approvalStatus: 'Approved',
				completedAt: new Date().toISOString(),
				sentWithoutSnappyGrant: sentWithoutSnappyGrant
			};

			const { data } = await props.client.mutate({
				mutation: UPDATE_BADGE_ASSIGNMENT,
				variables: { input: updateInput }
			});

			const requestBody = {
				approvalStatus: 'Approved'
			};
			
			try {
				await approvalRewardsService.updateApprovalStatus(recordId, requestBody);
			} catch (apiError) {
				console.error('Error updating approval status via API:', apiError);
			}

			if (data?.updateBadgeAssignment) {
				if (record.costInCurrency > 0) {
					await updateUserRecognitionEvents(record, overrideBudget);
				}

				await handlePostApprovalActions(record, sentWithoutSnappyGrant);

				setIsDecline(false);
				setIsApprove(false);
				setIsBudgetModal(false);
				message.success('Recognition approved successfully');
			} else {
				message.error('Failed to approve recognition');
			}
		} catch (error) {
			console.error('Error approving recognition:', error);
			message.error('Failed to approve recognition');
		}
	};

	const handlePostApprovalActions = async (record, sentWithoutSnappyGrant) => {
		try {
			const recipient = record.rawData?.user;
			const company = recipient?.company;
			
			const isRecognitionMode = company?.recognitionSettings?.rewardsEarnedMechanism === 'recognition';

			if (isRecognitionMode && !sentWithoutSnappyGrant && record.snappyGrantId) {
				await createSnappyGiftForRecipient(record);
			}

		} catch (error) {
			console.error('Error in post approval actions:', error);
		}
	};

	const createSnappyGiftForRecipient = async (record) => {
		try {
			const snappyGiftInput = {
				userId: record.recipientId,
				campaignTierId: record?.badge?.snappyGrant?.campaignTierId,
				snappyGrantId: record.snappyGrantId,
				badgeAssignmentId: record.id,
				status: 'pending'
			};

			const { data } = await props.client.mutate({
				mutation: CREATE_SNAPPY_GIFT,
				variables: { input: snappyGiftInput }
			});

			if (data?.createSnappyGift) {
				console.log('Snappy gift created for recipient:', data.createSnappyGift);
			}
		} catch (error) {
			console.error('Error creating snappy gift:', error);
		}
	};

	const updateUserRecognitionEvents = async (record, adminOverride = false) => {
		try {
			const userId = record.recipientId;
			const costInCurrency = record.costInCurrency || 0;
			const baseSnappyBudget = currentUser?.company?.recognitionSettings?.baseSnappyBudget || 0;
			
			const currentRecognitionEvents = record.rawData?.user?.recognitionEvents;
			const currentBudget = currentRecognitionEvents?.currentBudget !== undefined
				? currentRecognitionEvents.currentBudget
				: baseSnappyBudget;
			
			const newBudget = adminOverride ? currentBudget : currentBudget - costInCurrency;
			
			const newTransaction = {
				adminOverride: adminOverride,
				approvalResult: true,
				budgetAfterTotal: newBudget,
				budgetBeforeTotal: currentBudget,
				completedById: currentUser.id,
				completedDate: new Date().toISOString(),
				createdDate: new Date().toISOString(),
				pendingApproval: false,
				recognitionAmount: costInCurrency,
				recognitionId: record.id,
				recognitionName: record.badgeName,
				sentById: record.createdBy,
				sentByManager: false,
				sentReason: 'Badge Assignment Approval',
				snappyCampaignTierId: record.rawData?.badge?.snappyGrant?.id || null
			};

			const recognitionEventsBase = currentRecognitionEvents || {
				currentBudget: baseSnappyBudget,
				amountPending: 0,
				additionalBudgetTotal: 0,
				eventList: []
			};

			const updatedRecognitionEvents = {
				...recognitionEventsBase,
				currentBudget: newBudget,
				additionalBudgetTotal: (recognitionEventsBase.additionalBudgetTotal || 0) + (adminOverride ? costInCurrency : 0),
				lastUpdated: new Date().toISOString(),
				eventList: [...(recognitionEventsBase.eventList || []), newTransaction]
			};

			await props.client.mutate({
				mutation: UPDATE_USER,
				variables: {
					input: {
						id: userId,
						recognitionEvents: updatedRecognitionEvents
					}
				}
			});
		} catch (error) {
			console.log('Error updating user recognition events:', error);
		}
	};

	const handleApprovalClick = (record) => {
		setPendingRecordId(record?.id);
		setPendingRecord(record);
		setIsApprove(true);
	};

	const handleDeclineClick = (record) => {
		setPendingRecordId(record?.id);
		setPendingRecord(record);
		setIsDecline(true);
	};
	return (
		<div>
			<div className="page-title">
				<h2 className="page-heading">
					All Recognitions
				</h2>
			</div>
			{currentUser?.company?.recognitionSettings === 'milestone'
			? (<motion.div className="my-recognitions-container">
				<div className="row">
					<div className="col-md-6 col-12">
						<RewardPointsAndBonusContainer
							title="Rewards Summary"
							summaryDetals={badgeSummary}
						/>
					</div>
					<div className="col-md-6 col-12">
						<RewardPointsAndBonusContainer
							title="Engagement"
							summaryDetals={engagementSummary}
						/>
					</div>
				</div>
			</motion.div>)
			: (<motion.div style={{ display: 'flex', flexDirection: 'row', width: '100%', gap: 16, marginBottom: 20}}>
				<div className="contact-card" style={{ backgroundColor: 'white', padding: '16px', border: '1px solid #E5E5E5', borderRadius: '10px', minHeight: '156px', width: '50%' }}>
					<div className="contact-card-inner">
					<div className="contact-card-title">
						<p style={{ fontSize: '14px', fontWeight: '800', color: 'var(--dove-gray)' }}>Recognition Statuses</p>
					</div>
						{approvalStatusSection}
					</div>
				</div>
				<div className="contact-card" style={{ backgroundColor: 'white', padding: '16px', border: '1px solid #E5E5E5', borderRadius: '10px', minHeight: '156px', width: '50%' }}>
					<div className="contact-card-inner">
						<div className="contact-card-title">
							<p style={{ fontSize: '14px', fontWeight: '800', color: 'var(--dove-gray)' }}>Recognition Totals</p>
						</div>
						{rewardPointsSection}
					</div>
				</div>
			</motion.div>
			)}
			<ApprovedContainer
				tableData={pendingAssignments}
				isPending={true}
				isApprove={isApprove}
				isDecline={isDecline}
				setIsDecline={setIsDecline}
				setIsApprove={setIsApprove}
				handleApprovalClick={handleApprovalClick}
				handleDeclineClick={handleDeclineClick}
				handleApproveAndDeclined={handleApproveAndDeclined}
				getJustificationContent={getJustificationContent}
				pendingRecordId={pendingRecordId}
				pendingRecord={pendingRecord}
			/>
			{approvedDeclinedAssignments && (
				<ApprovedContainer
					tableData={approvedDeclinedAssignments}
					isPendingReward={false}
					pendingRecordId={pendingRecordId}
					getJustificationContent={getJustificationContent}
				/>
			)}
			
			<Modal
				destroyOnClose
				width={500}
				open={isBudgetModal}
				title="Budget Override Required"
				footer={null}
				className="confirmation-modal"
				onCancel={() => {
					setIsBudgetModal(false);
				}}
			>
				<div>
					<p className="text-center modal-detail" style={{ marginBottom: '20px' }}>
						Snappy Grant would be more than the employee's total budget, do you want to send this without a gift attached?
					</p>
					<div className="text-center" style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
						<Button
							className="approve-btn"
							type="primary"
							onClick={() => approveWithGift(pendingRecordId, pendingRecord, true, false)}
							style={{ marginRight: '10px' }}
						>
							Yes
						</Button>
						<Button
							className="approve-btn"
							danger={true}
							onClick={() => props.history.push('/employee-manager')}
							style={{ marginRight: '10px' }}
						>
							No, Override the budget
						</Button>
						<Button
							className="decline-btn"
							ghost={true}
							onClick={() => setIsBudgetModal(false)}
						>
							No
						</Button>
					</div>
				</div>
			</Modal>
		</div>
	);
};

export default withRouter(withApollo(ApprovalRewardsContainer));
